import React, { useState } from 'react';
import { useWhatsNew } from './useWhatsNew';
import WhatsNewDrawer from './WhatsNewDrawer';

/**
 * The "What's New" entry point — a quiet, text-only link pinned to the bottom
 * of the sidebar, above the user card. No icon; small type. An unobtrusive
 * marker (a dot AND the word "New", so it survives print and colour-blindness)
 * shows until the user opens the timeline.
 */

const SE = {
  paper: '#F2EADB',
  ink: '#2A1F17',
  title: '#0B3B2E',
  champagne: '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  hoverWash: 'rgba(11,59,46,0.05)',
  muted: '#7C8A82',
} as const;

export interface WhatsNewLinkProps {
  collapsed: boolean;
  activeDepartment?: string | null;
}

const WhatsNewLink: React.FC<WhatsNewLinkProps> = ({ collapsed, activeDepartment }) => {
  const [open, setOpen] = useState(false);
  const { hasUnseen, markSeen, latestRelease } = useWhatsNew();

  const handleOpen = () => {
    markSeen();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        title="What's New"
        aria-label={hasUnseen ? "What's New — new updates" : "What's New"}
        className={`group relative flex items-center w-full rounded-lg transition-all duration-200 ${
          collapsed ? 'justify-center px-0 py-1.5' : 'justify-between px-2.5 py-1.5'
        }`}
        style={{ backgroundColor: 'transparent', color: SE.muted }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = SE.hoverWash;
          e.currentTarget.style.color = SE.title;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = SE.muted;
        }}
      >
        {collapsed ? (
          <span className="relative text-[10px] font-semibold tracking-tight">
            New
            {hasUnseen && (
              <span
                aria-hidden
                className="absolute -top-1 -right-2 w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: SE.champagne }}
              />
            )}
          </span>
        ) : (
          <>
            <span className="text-[11.5px] font-medium tracking-tight">
              What&rsquo;s New &amp; Updates
            </span>

            {hasUnseen ? (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.06em]"
                style={{ color: SE.title }}
              >
                <span
                  aria-hidden
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: SE.champagne }}
                />
                New
              </span>
            ) : (
              latestRelease && (
                <span className="text-[10px]" style={{ color: SE.muted }}>
                  {latestRelease.version}
                </span>
              )
            )}
          </>
        )}
      </button>

      <WhatsNewDrawer
        open={open}
        onClose={() => setOpen(false)}
        activeDepartment={activeDepartment}
      />
    </>
  );
};

export default WhatsNewLink;
